import React, { useState, useEffect } from 'react'; // Importing React and hooks
import apiCreateCompanies from '../../services/createCompanies.services.js'; // Importing API service 
import { ValidateEmail } from '../../services/emailvalidations'; // Importing function for email validation
import {
    TextField, Button, Box, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Alert, Card, CardContent, Divider, Snackbar,
    FormHelperText, Tooltip, IconButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import countriesData from '../../utils/countries'; //Importing country data 
import { FluxCircularProgress } from '@meltwater/flux-web-components-react'; //Importing the loading spinner
import styles from './createCompanies.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';

//default data required for creating a company and an initial user    
const initialFormData = {
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    companyCountryCode: '',
    ssoRequired: true,
    productIds: 'default',
    buddyAccount: true
};

const Companies = () => {
    const [formData, setFormData] = useState(initialFormData); // State variable to track form data 
    const [dialogOpen, setDialogOpen] = useState(false); // State variable to track dialog box state
    const [responseDialogData, setResponseDialogData] = useState(null); // State varibale for the response data dialog
    const [emailError, setEmailError] = useState(''); // State variable to track invalid email
    const [isLoading, setIsLoading] = useState(false); // State variable to track loading state
    const [isSubmitting, setIsSubmitting] = useState(false); // State variable to track form submission
    const [isAdmin, setIsAdmin] = useState(false); // State variable to track admin permissions
    const [bundles, setBundles] = useState([]); // State variable for bundles
    const [copySuccess, setCopySuccess] = useState(false); // State variable to track copy success
    const navigate = useNavigate(); // Add navigate hook at the top level

    const token = getJwtToken();
    useEffect(() => {
        if (token) {
            const decodedToken = jwt_decode(token);
            if (decodedToken.companiespermissions.includes('admin') || decodedToken.companiespermissions.includes('write')) {
            setIsAdmin(true);
            } else {
                setIsAdmin(false);
            }
        }
    }, [token]); // Only re-run the effect if token changes


    useEffect(() => {
        // Fetch bundles when the component mounts 
        fetchBundles();
    }, []);

    // fetching bundles
    const fetchBundles = async () => {
        try {
            const response = await apiCreateCompanies.getBundle();
            const bundleData = response.data; // get only "data".
            if (Array.isArray(bundleData)) {
                setBundles(bundleData);
            } else {
                console.error('Bundle data is not an array:', bundleData);
            }
        } catch (error) {
            console.error('Error fetching bundle data', error.message);
        } 
    };
  

    // Handling form input change 
    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
        if (isSubmitting && name === 'email' && value && !ValidateEmail(value)) {
            setEmailError('Invalid email address');
        } else {
            setEmailError('');
        }
        // Update productId
        if (name === 'bundleTitle') {
            setFormData({ ...formData, productIds: value });
        }
    };
    
    // Open dialog box
    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    // Close dialog box
    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    // Handling the form
    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        // Validate form fields
        const validationErrors = validateForm(formData);
        if (validationErrors.length > 0) {
            setEmailError(validationErrors[0]);
            return;
        }
        if (!ValidateEmail(formData.email)) {
            setEmailError('Invalid email address');
            setIsSubmitting(false); 
            return;
        }
        try {
            setIsLoading(true); // Set loading state to true before making the API call
            const response = await apiCreateCompanies.createCompany(formData);
            console.log('Response:', response);
            setResponseDialogData(response);
            handleDialogOpen();
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false); // Set loading state back to false after the API call completes
            setIsSubmitting(false);
        }
    };

    const validateForm = (formData) => {
        const errors = [];
        // Array of fields to exclude from validation
        const excludedFields = ['ssoRequired'];
        Object.entries(formData).forEach(([key, value]) => {
            // Check if the field is not in the excluded list and it is empty
            if (!excludedFields.includes(key) && !value) {
                errors.push(`${key.charAt(0).toUpperCase() + key.slice(1)} is required`);
            }
        });
        if (!ValidateEmail(formData.email)) {
            errors.push('Invalid email address');
        }
        return errors;
    };

    // clear form data when dialog is closed
    useEffect(() => {
        if (!dialogOpen) {
            setFormData(initialFormData);
            setEmailError('');
        }
    }, [dialogOpen]);

    if (!isAdmin) {
        return (
            <div className={styles.Companies}>
                <Typography variant="h5" color="error">
                   {/* You do not have permission to access this page. */}
                </Typography>
            </div>
        );
    }

    return (
        <div className={styles.Companies}>
            <div className={styles.formContainer}>
                    <Box display="flex" alignItems="center" mb={3} position="relative">
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        className={styles.backButton}
                        variant="text"
                        sx={{ position: 'absolute', left: 0, zIndex: 1 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" className={styles.formTitle} sx={{ width: '100%', textAlign: 'center' }}>
                        Create New Company & Initial User
                    </Typography>
                </Box>
                <form onSubmit={handleSubmit} noValidate>
                    <Card className={styles.formCard} variant="outlined">
                        <CardContent>
                            <Typography variant="h6" className={styles.sectionTitle}>
                                User Details
                            </Typography>
                            <Divider style={{ marginBottom: '20px' }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="firstName"
                                        label="First Name"
                                        variant="outlined"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={isSubmitting && !formData.firstName}
                                        helperText={isSubmitting && !formData.firstName ? "First name is required" : ""}
                                        className={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="lastName"
                                        label="Last Name"
                                        variant="outlined"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        error={isSubmitting && !formData.lastName}
                                        helperText={isSubmitting && !formData.lastName ? "Last name is required" : ""}
                                        className={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="email"
                                        label="User Email"
                                        variant="outlined"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        error={isSubmitting && (emailError || !formData.email)}
                                        helperText={isSubmitting && (emailError || !formData.email) ? (emailError || "Email is required") : ""}
                                        className={styles.textField}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card className={styles.formCard} variant="outlined">
                        <CardContent>
                            <Typography variant="h6" className={styles.sectionTitle}>
                                Company Details
                            </Typography>
                            <Divider style={{ marginBottom: '20px' }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        id="companyName"
                                        label="Company Name"
                                        variant="outlined"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        error={isSubmitting && !formData.companyName}
                                        helperText={isSubmitting && !formData.companyName ? "Company name is required" : ""}
                                        className={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl 
                                        fullWidth 
                                        variant="outlined" 
                                        error={isSubmitting && !formData.companyCountryCode}
                                        required
                                        className={styles.textField}
                                    >
                                        <InputLabel id="company-country-code-label">Country</InputLabel>
                                        <Select
                                            labelId="company-country-code-label"
                                            id="companyCountryCode"
                                            name="companyCountryCode"
                                            value={formData.companyCountryCode}
                                            onChange={handleChange}
                                            label="Country"
                                        >
                                            {countriesData.map((country, index) => (
                                                <MenuItem key={index} value={country.val}>{country.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {isSubmitting && !formData.companyCountryCode && 
                                            <FormHelperText className={styles.errorMessage}>Country is required</FormHelperText>
                                        }
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card className={styles.formCard} variant="outlined">
                        <CardContent>
                            <Typography variant="h6" className={styles.sectionTitle}>
                                Bundles
                            </Typography>
                            <Divider style={{ marginBottom: '20px' }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined" className={styles.textField}>
                                        <InputLabel id="bundle-title-label">Bundle (Optional)</InputLabel>
                                        <Select
                                            labelId="bundle-title-label"
                                            id="bundleTitle"
                                            name="bundleTitle"
                                            value={formData.productIds}
                                            onChange={handleChange}
                                            label="Bundle (Optional)"
                                        >
                                            {bundles.map((bundle, index) => (
                                                <MenuItem key={index} value={bundle.productId}>{bundle.title}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading}
                        className={styles.submitButton}
                    >
                        {isLoading ? 'Creating...' : 'Create Company'}
                    </Button>
                </form>
            </div>
            
            {/* Loading Dialog */}
            <Dialog open={isLoading} aria-labelledby="loading-dialog-title">
                <DialogTitle id="loading-dialog-title">Creating Company</DialogTitle>
                <DialogContent className={styles.dialogContent}>
                    <FluxCircularProgress indeterminate size="100" className={styles.loadingSpinner}>In Progress...</FluxCircularProgress>
                    <DialogContentText style={{ marginTop: '20px' }}>
                        Please wait while we set up your new company and user...
                    </DialogContentText>
                </DialogContent>
            </Dialog>

            {/* Error Snackbar */}
            <Snackbar 
                open={!!emailError && isSubmitting} 
                autoHideDuration={6000} 
                onClose={() => setEmailError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setEmailError('')} severity="error" variant="filled">
                    {emailError}
                </Alert>
            </Snackbar>
            
            {/* Response Dialog */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleDialogClose}
                maxWidth="md"
            >
                <DialogTitle>New Company & Initial User Created</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText component="div">
                        {responseDialogData && (
                            <Box component="pre" className={styles.responseDialogContent}>
                                {JSON.stringify(responseDialogData, null, 2)}
                            </Box>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Tooltip title="Copy to clipboard">
                        <IconButton
                            onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(responseDialogData, null, 2));
                                setCopySuccess(true);
                            }}
                            className={styles.dialogButton}
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                    <Button 
                        onClick={handleDialogClose} 
                        className={styles.dialogButton}
                        variant="contained"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Copy Success Snackbar */}
            <Snackbar 
                open={copySuccess} 
                autoHideDuration={6000} 
                onClose={() => setCopySuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setCopySuccess(false)} severity="success" variant="filled">
                    JSON copied to clipboard!
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Companies;
